import { CategoryChart, Unit } from "../processors";
import React, { useCallback, useMemo } from "react";

import { FormattingFn, StdCountUp } from "../StdCountUp";
import _ from "lodash";

import styles from "./Figure.module.scss";
import { isValueExceptionMessage } from "../misc";
import { formatNumber } from "../../utils";

export interface FigureProps {
    header?: string | JSX.Element;
    prefix?: string | JSX.Element;
    suffix?: string | JSX.Element;
    unitInSuffix?: Unit;
    value: number | string;
    footer?: string | JSX.Element;
    className?: string;
    decimals?: number;
    figureStyle?: React.CSSProperties;
    unit?: Unit;
    categoryChart?: CategoryChart;
}

export const Figure: React.FC<FigureProps> = ({
    header,
    prefix,
    suffix,
    value,
    footer,
    className,
    unitInSuffix,
    decimals = 0,
    figureStyle,
    unit,
    categoryChart
}) => {
    const unitClass = useMemo(() => {
        if (unitInSuffix == null || unitInSuffix?.trim() === "") return "";
        if (unitInSuffix === "%") return "percent";
        return _.camelCase(unitInSuffix);
    }, [unitInSuffix]);

    const isValueException = useMemo(() => {
        return isValueExceptionMessage((value ?? "").toString());
    }, [value]);

    const formattingFn: FormattingFn = useCallback(
        (value: number) => {
            return formatNumber(value, undefined, unit, categoryChart);
        },
        [unit, categoryChart]
    );

    return (
        <div className={`${className ?? ""} std-figure`}>
            {header != null && <div className="headline">{header}</div>}
            {
                <div className="content" style={figureStyle}>
                    {!isNaN(value as number) && prefix != null && <label className="mr-2">{prefix}</label>}
                    {isNaN(value as number) && (
                        <label className={`${isValueException ? "value-exception" : ""}`}>{value}</label>
                    )}
                    {!isNaN(value as number) && (
                        <StdCountUp
                            decimals={decimals}
                            value={value as number}
                            variant="big"
                            formattingFn={formattingFn}
                        />
                    )}
                    {!isNaN(value as number) && suffix != null && (
                        <label className={`ml-2 ${styles.suffix} ${styles?.[unitClass] ?? ""}`}>{suffix}</label>
                    )}
                </div>
            }
            {footer != null && <div className="footer">{footer}</div>}
        </div>
    );
};
